import React from 'react';
import { Box, Typography, Button, Breadcrumbs, Link, Paper, Fade } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

interface BreadcrumbItem {
  title: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  action?: {
    text: string;
    icon?: React.ReactNode;
    onClick: () => void;
  };
}

export default function PageHeader({ title, subtitle, breadcrumbs, action }: PageHeaderProps) {
  return (
    <Fade in timeout={600}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            animation: 'pulse 4s ease-in-out infinite',
          },
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' },
          },
        }}
      >
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs 
            sx={{ 
              mb: 2,
              position: 'relative',
              zIndex: 1,
              '& .MuiBreadcrumbs-separator': {
                color: 'rgba(255,255,255,0.7)',
              }
            }}
            separator={<NavigateNextIcon fontSize="small" />}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return isLast ? (
                <Typography 
                  key={index} 
                  sx={{ 
                    color: 'white',
                    fontWeight: 600,
                  }}
                >
                  {crumb.title}
                </Typography>
              ) : (
                <Link 
                  key={index} 
                  component={RouterLink} 
                  to={crumb.href || '#'} 
                  underline="hover" 
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: 'white',
                    }
                  }}
                >
                  {crumb.title}
                </Link>
              );
            })}
          </Breadcrumbs>
        )}
        
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{
                fontWeight: 700,
                mb: subtitle ? 1 : 0,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                variant="subtitle1" 
                sx={{
                  opacity: 0.95,
                  fontSize: '1.1rem',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          
          {action && (
            <Button 
              variant="contained"
              startIcon={action.icon} 
              onClick={action.onClick}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              {action.text}
            </Button>
          )}
        </Box>
      </Paper>
    </Fade>
  );
}